import type { Arsenal } from "../../engine/arsenal";
import type { Player } from "../../engine/player";
import type { Weapon } from "../../engine/weapon";
import type { Event } from "../../engine/events/event";
import { formatEvent } from "../eventBinding";
import { getPickupIconFilename } from "../pickupIconMap";
import { isRechargeable } from "../../engine/interfaces";

/** Families 1..7, plus dev family (id 0) appended when it has weapons. */
export const HUD_WEAPON_SLOT_COUNT = 7;

/** Fraction of max HP below which both low-health cues (border flash + vignette) activate. */
export const LOW_HEALTH_THRESHOLD = 0.4;

export interface WeaponSlot {
	familyId: number;
	/** null = empty placeholder slot. */
	weapon: Weapon | null;
	familySize: number;
	isActive: boolean;
	/** -1 when infinite or empty. */
	ammo: number;
	isInfiniteAmmo: boolean;
	/** Remaining recharge turns; 0 when ready or empty. */
	cooldownTurns: number;
	iconFilename: string | null;
	displayName: string;
}

export interface HealthBarData {
	hp: number;
	maxHp: number;
	/** 0..100, clamped so negative hp renders as empty. */
	fillPercent: number;
	label: string;
	/** True when alive and at/below LOW_HEALTH_THRESHOLD. */
	isLow: boolean;
}

export interface EventLogEntry {
	lines: { text: string; colorKey: string; bold: boolean }[];
}

/** Pure — engine state in, plain data out. */
export class HudViewModel {
	computeWeaponSlots(arsenal: Arsenal): WeaponSlot[] {
		const selectedFamilyId = arsenal.selectedFamilyId;
		const slots: WeaponSlot[] = [];

		for (let familyId = 1; familyId <= HUD_WEAPON_SLOT_COUNT; familyId++) {
			slots.push(this.buildSlot(arsenal, familyId, selectedFamilyId));
		}

		const devFamily = arsenal.getFamilyWeapons(0);
		if (devFamily.length > 0) {
			slots.push(this.buildSlot(arsenal, 0, selectedFamilyId));
		}

		return slots;
	}

	private buildSlot(arsenal: Arsenal, familyId: number, selectedFamilyId: number): WeaponSlot {
		const familyWeapons = arsenal.getFamilyWeapons(familyId);
		if (familyWeapons.length === 0) {
			return {
				familyId,
				weapon: null,
				familySize: 0,
				isActive: false,
				ammo: -1,
				isInfiniteAmmo: false,
				cooldownTurns: 0,
				iconFilename: null,
				displayName: "",
			};
		}

		const idx = arsenal.selectedIndexInFamily.get(familyId) ?? 0;
		const weapon = familyWeapons[idx] ?? familyWeapons[0];

		return {
			familyId,
			weapon,
			familySize: familyWeapons.length,
			isActive: familyId === selectedFamilyId,
			ammo: weapon.infinite ? -1 : weapon.ammo,
			isInfiniteAmmo: weapon.infinite,
			cooldownTurns: isRechargeable(weapon) ? weapon.getCooldownTurns() : 0,
			iconFilename: getPickupIconFilename(weapon),
			displayName: weapon.getName(),
		};
	}

	computeHealthBar(player: Player): HealthBarData {
		const displayHp = Math.max(0, player.hp);
		const ratio = player.hp / player.maxHp;
		const fillPercent = Math.max(0, Math.min(100, ratio * 100));
		return {
			hp: player.hp,
			maxHp: player.maxHp,
			fillPercent,
			label: `${displayHp} / ${player.maxHp}`,
			isLow: player.hp > 0 && ratio <= LOW_HEALTH_THRESHOLD,
		};
	}

	computeEventLogEntries(events: Event[]): EventLogEntry[] {
		const entries: EventLogEntry[] = [];
		for (const event of events) {
			const segments = formatEvent(event);
			if (segments.length === 0) continue;
			entries.push({
				lines: segments.map((seg) => ({
					text: seg.text,
					colorKey: seg.color,
					bold: seg.bold,
				})),
			});
		}
		return entries;
	}
}
